import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AIChartTooltip from "../components/AIChartTooltip";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from "recharts";
import PlotSummary from "../components/PlotSummary";
import { useTheme, getColors } from "../ThemeContext";  

import { API } from "../config/api";

const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525", border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d", pink:"#ff4da6", purple:"#a855f7",
  blue:"#3b9eff", red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a",
  text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }  
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";
const CHART_MARGIN = { top: 8, right: 14, left: 8, bottom: 36 };
const LEGEND_PROPS = { verticalAlign:"top", align:"right", iconSize:8, wrapperStyle:{ fontFamily:MONO, fontSize:10, paddingBottom:6 } };

function getMethodColor() {                
  return { PLUNGER: C.blue, GAPL: C.cyan, PAGL: C.amber, GASLIFT: C.purple };
}
const METHOD_LABEL = { PLUNGER: "Plunger Lift", GAPL: "GAPL", PAGL: "PAGL", GASLIFT: "Gas Lift" };

function TTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.panel, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 12px", fontFamily:MONO, fontSize:11 }}>
      {label != null && <div style={{ color:C.muted, marginBottom:4 }}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{ color:p.color }}>{p.name}: {typeof p.value==="number"?p.value.toLocaleString(undefined,{maximumFractionDigits:2}):p.value}</div>
      ))}
    </div>
  );
}
function Card({ title, subtitle, children, height, accentColor, summarySubtitle, lensId }) {
  const ac = accentColor || C.cyan;
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"20px 16px", position:"relative" }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      {height ? <div style={{ height }}>{children}</div> : children}
      {height && (
        <PlotSummary inline lensId={lensId} title={title} subtitle={summarySubtitle || (typeof subtitle==="string" ? subtitle : "")} accent={ac}>{children}</PlotSummary>
      )}
    </div>
  );
}

function ruleStatusColor(status) {
  return { pass:C.green, marginal:C.amber, fail:C.red }[status] || C.muted;
}
function RuleStatusIcon({ status, color }) {
  if (status === "pass") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  );
  if (status === "marginal") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  );
}
function RulesPanel({ rules, accentColor }) {
  if (!rules || rules.length === 0) return null;
  const passCount = rules.filter(r=>r.status==="pass").length;
  const margCount = rules.filter(r=>r.status==="marginal").length;
  const failCount = rules.filter(r=>r.status==="fail").length;
  return (
    <Card title="Physics-Based Rules Validation" subtitle="Hard physical constraints checked against every cycle" accentColor={accentColor||C.orange}>
      <div style={{ display:"flex", gap:16, marginBottom:14 }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.green }}>◆ Pass: {passCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>▲ Marginal: {margCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.red }}>■ Fail: {failCount}</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {rules.map(r => {
          const sc = ruleStatusColor(r.status);
          return (
            <div key={r.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:4 }}>
                <RuleStatusIcon status={r.status} color={sc}/>
                <span style={{ fontFamily:SANS, fontWeight:700, fontSize:12, color:C.text }}>{r.name}</span>
                <span style={{ fontFamily:MONO, fontSize:8, letterSpacing:1, textTransform:"uppercase", color:sc, background:`${sc}18`, border:`1px solid ${sc}44`, borderRadius:3, padding:"2px 7px" }}>{r.status}</span>
                {r.equation && <span style={{ fontFamily:MONO, fontSize:9, color:C.cyan, background:`${C.cyan}12`, border:`1px solid ${C.cyan}33`, borderRadius:3, padding:"2px 7px" }}>{r.equation}</span>}
                <span style={{ fontFamily:MONO, fontSize:10, color:C.cyan, marginLeft:"auto" }}>{r.value}</span>
              </div>
              <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.6 }}>{r.explanation}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function plungerRowFromSummary(summary, wellCount) {
  if (!summary) return null;
  const get = (k) => summary[k]?.mean ?? null;
  const liquid = get("LiquidVolume_bbl");
  const gas = get("FlowRateAtClose"); 
  const glr = (liquid > 0 && gas != null)
    ? Math.round((gas * 1000 / liquid) * 10) / 10
    : null;
  return {
    method: "PLUNGER",
    avg_liquid_bbl_per_day_equiv: liquid,
    avg_gas_mcf_per_day_equiv: gas,
    avg_glr: glr,
    success_rate_pct: summary.arrival_compliance_pct ?? null, 
    avg_load_factor: get("ShutInEfficiency"),
    wells: wellCount ?? null,
    _isReal: true,
  };
}

export default function MethodComparisonPage() {
  const { isDark } = useTheme();         
  applyTheme(getColors(isDark));          
  const METHOD_COLOR = getMethodColor(); 
  const [methods, setMethods] = useState([]);
  const [physics, setPhysics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    Promise.allSettled([
      axios.get(`${API}/method-comparison`),
      axios.get(`${API}/cycles/summary`),
      axios.get(`${API}/wells`),
      axios.get(`${API}/method-comparison/physics-validation`),
    ]).then(([mcRes, sumRes, wellsRes, physRes]) => {
      const rows = mcRes.status === "fulfilled" ? (mcRes.value.data.methods || []) : [];
      const wellCount = wellsRes.status === "fulfilled" ? (wellsRes.value.data.wells || []).length : null;
      const plunger = sumRes.status === "fulfilled" ? plungerRowFromSummary(sumRes.value.data, wellCount) : null;
      setMethods(plunger ? [plunger, ...rows] : rows);
      setPhysics(physRes.status === "fulfilled" ? physRes.value.data : null);
      if (mcRes.status !== "fulfilled" && sumRes.status !== "fulfilled") {
        setError("Could not load comparison data from either service.");
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ fontFamily:SANS }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
      </div>

      {loading && <div style={{ color:C.muted, fontFamily:MONO }}>Loading…</div>}
      {error && <div style={{ color:C.red, fontFamily:MONO }}>{error}</div>}

      {!loading && !error && methods.length > 0 && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${methods.length}, 1fr)`, gap:14, marginBottom:20 }}>
            {methods.map(row => (
              <div key={row.method} style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${METHOD_COLOR[row.method]||C.cyan}`, borderRadius:12, padding:"16px 18px" }}>
                <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:METHOD_COLOR[row.method]||C.text, marginBottom:8 }}>
                  {METHOD_LABEL[row.method] || row.method}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, fontFamily:MONO, fontSize:11 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.muted }}>Liquid /day</span><span style={{ color:C.text }}>{row.avg_liquid_bbl_per_day_equiv ?? "-"} bbl</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.muted }}>Gas /day</span><span style={{ color:C.text }}>{row.avg_gas_mcf_per_day_equiv ?? "-"} MCF</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.muted }}>GLR</span><span style={{ color:C.text }}>{row.avg_glr ?? "-"} scf/bbl</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.muted }}>Success rate</span><span style={{ color:C.text }}>{row.success_rate_pct ?? "-"}{row.success_rate_pct!=null?"%":""}</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.muted }}>Load factor</span><span style={{ color:C.text }}>{row.avg_load_factor ?? "-"}</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.muted }}>Wells</span><span style={{ color:C.text }}>{row.wells ?? "-"}</span></div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18, marginBottom:18 }}>
            <Card title="GLR by Method" lensId="glr" summarySubtitle="Average gas-to-liquid ratio (scf/bbl) for each artificial lift method, one bar per method; lower GLR means more liquid produced per unit of gas, higher means gas-heavy production" subtitle="Gas-to-liquid ratio, scf/bbl: lower means more liquid produced per unit of gas" accentColor={C.cyan} height={340}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={methods.map(r=>({ method:METHOD_LABEL[r.method]||r.method, value:r.avg_glr }))} margin={CHART_MARGIN}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                    <XAxis
                      dataKey="method"
                      tick={{ fill:C.muted, fontSize:10, fontFamily:MONO }}
                      tickLine={false}
                      label={{ value: "Lift Method", position: "insideBottom", offset: -18, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                    />
                    <YAxis
                      width={48}
                      tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                      label={{ value: "GLR (scf/bbl)", angle: -90, position: "insideLeft", dx: -4, dy: 30, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                    />
                    <Tooltip
                      trigger="hover"
                      isAnimationActive={false}
                      content={<AIChartTooltip fieldKey="avg_glr" contextData={{ chart:"GLR by Method" }} />}
                    />
                    <Bar dataKey="value" name="GLR scf/bbl" radius={[3,3,0,0]} isAnimationActive={false}>
                      {methods.map((r,i)=><Cell key={i} fill={METHOD_COLOR[r.method]||C.cyan}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>

            <Card title="Gas Rate by Method" subtitle="MCF/day equivalent" lensId="method-compare" summarySubtitle="Average gas rate (MCF per day equivalent) for each artificial lift method, one bar per method; compare which methods produce the most and least gas" accentColor={C.teal} height={340}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={methods.map(r=>({ method:METHOD_LABEL[r.method]||r.method, value:r.avg_gas_mcf_per_day_equiv }))} margin={CHART_MARGIN}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                  <XAxis
                    dataKey="method"
                    tick={{ fill:C.muted, fontSize:10, fontFamily:MONO }}
                    tickLine={false}
                    label={{ value: "Lift Method", position: "insideBottom", offset: -18, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  />
                  <YAxis
                    width={48}
                    tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                    label={{ value: "Gas Rate (MCF/d)", angle: -90, position: "insideLeft", dx: -4, dy: 30, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  />
                  <Tooltip
                    trigger="hover"
                    isAnimationActive={false}
                    content={<AIChartTooltip fieldKey="avg_gas_mcf_per_day_equiv" contextData={{ chart:"Gas Rate by Method" }} />}
                  />
                  <Bar dataKey="value" name="Gas MCF/d" radius={[3,3,0,0]} isAnimationActive={false}>
                    {methods.map((r,i)=><Cell key={i} fill={METHOD_COLOR[r.method]||C.cyan}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card title="Success Rate by Method" lensId="method-compare" summarySubtitle="Success rate (%) for each artificial lift method, one bar per method, using each method's own definition of success (plunger arrival, assist effective, or above critical rate); compare how reliably each method works" subtitle={`Plunger arrival / assist-effective / above-critical-rate, method-specific definition of "success"`} accentColor={C.green} height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={methods.map(r=>({ method:METHOD_LABEL[r.method]||r.method, value:r.success_rate_pct }))} margin={CHART_MARGIN}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis
                  dataKey="method"
                  tick={{ fill:C.muted, fontSize:10, fontFamily:MONO }}
                  tickLine={false}
                  label={{ value: "Lift Method", position: "insideBottom", offset: -18, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                />
                <YAxis
                  width={48}
                  tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                  label={{ value: "Success Rate (%)", angle: -90, position: "insideLeft", dx: -4, dy: 40, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                />
                <Tooltip
                  trigger="hover"
                  isAnimationActive={false}
                  content={<AIChartTooltip fieldKey="success_rate_pct" contextData={{ chart:"Success Rate by Method" }} />}
                />
                <Bar dataKey="value" name="Success %" radius={[3,3,0,0]} isAnimationActive={false}>
                  {methods.map((r,i)=><Cell key={i} fill={METHOD_COLOR[r.method]||C.green}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <div style={{ marginTop:18 }}>
            <RulesPanel rules={physics?.rules} accentColor={C.orange}/>
          </div>
        </>
      )}
    </div>
  );
}